import { useRef } from 'react';

import styles from './styles.module.scss';

const DIGIT_COUNT = 6;

// HORO-10509: 6 separate digit boxes, per Figma (verification screen) —
// not a single text input. Auto-advances focus on entry, moves back on
// backspace, and accepts a paste of the full code into any box (each box
// caps at 1 char via maxLength, so pasting multiple digits has to be
// handled explicitly via onPaste rather than relying on onChange —
// maxLength truncates the value before onChange ever sees it).
const OtpCodeInput = ({ value = '', onChange, disabled = false }) => {
    const inputRefs = useRef([]);
    const digits = value.padEnd(DIGIT_COUNT, ' ').split('').slice(0, DIGIT_COUNT);

    const setDigit = (index, char) => {
        const next = digits.slice();
        next[index] = char;
        onChange(next.join('').trimEnd());
    };

    const onDigitChange = (index) => (event) => {
        const raw = event.target.value.replace(/\D/g, '').slice(-1);
        setDigit(index, raw);
        if (raw && index < DIGIT_COUNT - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const onKeyDown = (index) => (event) => {
        if (event.key === 'Backspace' && !digits[index].trim() && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const onPaste = (event) => {
        const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
        if (!pasted) {
            return;
        }
        event.preventDefault();
        const next = pasted.slice(0, DIGIT_COUNT).split('');
        onChange(next.join(''));
        const lastIndex = Math.min(next.length, DIGIT_COUNT) - 1;
        inputRefs.current[lastIndex]?.focus();
    };

    return (
        <div className={styles.otpWrapper}>
            {digits.map((digit, index) => (
                <input
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    ref={(el) => {
                        inputRefs.current[index] = el;
                    }}
                    className={styles.otpBox}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    // Best-effort — iOS Safari's QuickType bar offers a
                    // code found in Mail (not just SMS) when a field is
                    // hinted this way, even split across separate boxes
                    // like this one. Not universally supported across
                    // browsers, but harmless where it isn't; `onPaste`
                    // below already handles the "paste the whole code at
                    // once" case this produces.
                    autoComplete="one-time-code"
                    value={digit.trim()}
                    disabled={disabled}
                    onChange={onDigitChange(index)}
                    onKeyDown={onKeyDown(index)}
                    onPaste={onPaste}
                />
            ))}
        </div>
    );
};

export default OtpCodeInput;
